import { yupResolver } from '@hookform/resolvers/yup'
import { getMessage } from '@limitless-minds/app-messages'
import { Button } from '@limitless-minds/ui-button'
import { Input } from '@limitless-minds/ui-input'
import { Paragraph } from '@limitless-minds/ui-text'
import { Stack, styled, useTheme } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { useSendResetPasswordEmail } from '@/data/auth'
import { useStore } from '@/store'
import { Error as IError } from '@/types/Error'
import ErrorMessage from '@/ui/atoms/ErrorMessage'
import StyledLink from '@/ui/atoms/StyledLink'
import Footer from '@/ui/molecules/Footer'
import HeaderWrapper from '@/ui/molecules/HeaderWrapper'
import Body from '@/ui/organisms/Body'
import InfoComponent from '@/ui/organisms/InfoComponent'

import { FIELDS, schema } from './schema'

const StyledDiv = styled(Stack)(`
  justify-content: center;
  align-items: center;
  align-self: center;
  width: 100%;
  height: 100%;
  gap: 2rem;
`)

type FormValues = {
  email: string
}

export const ForgotPasswordTemplate: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0)
  const [failedMessage, setFailedMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { setEmail } = useStore()

  const theme = useTheme()
  const { mutate: sendResetPasswordEmail } = useSendResetPasswordEmail()
  const { clearAll } = useStore()

  const handleStepUp = useCallback((): void => {
    setActiveStep((prev) => prev + 1)
  }, [])

  const handleBackStep = useCallback((): void => {
    setActiveStep((prev) => prev - 1)
  }, [])

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FIELDS.EMAIL]: '',
    },
  })

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    (data: FormValues) => {
      const { email } = data
      setEmail(email)
      setIsSubmitting(true)
      sendResetPasswordEmail(
        { body: { email } },
        {
          onSuccess: () => {
            setFailedMessage('')
            setIsSubmitting(false)
            handleStepUp()
          },
          onError: (error) => {
            setIsSubmitting(false)
            const errorResponse = error as IError
            const errorMessage = getMessage(
              errorResponse.response?.data?.error?.errorCode,
              errorResponse.response?.data?.error?.params
            )
            setFailedMessage(errorMessage)
          },
        }
      )
    },
    [handleStepUp, sendResetPasswordEmail, setEmail]
  )

  const steps = useMemo(() => {
    return [
      <StyledDiv key={'forgot-password-step1'} maxWidth='25rem'>
        <HeaderWrapper
          title='Reset your password'
          description='Enter the email you use for your account below and we’ll send an email with instructions.'
        />
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}
        >
          <Stack gap='0.75rem'>
            {failedMessage !== '' && <ErrorMessage>{failedMessage}</ErrorMessage>}
            <Controller
              name={FIELDS.EMAIL}
              control={control}
              render={({ field }) => (
                <Input
                  label={'Email'}
                  {...field}
                  error={!!errors?.[FIELDS.EMAIL]}
                  errorMessage={errors?.[FIELDS.EMAIL]?.message}
                />
              )}
            />
          </Stack>
          <Button type='submit' width='100%' disabled={isSubmitting}>
            Reset Password
          </Button>
        </form>
        <Link to='/login'>
          <Paragraph variant='p16' underlined color={theme.palette.role.primary}>
            Go Back
          </Paragraph>
        </Link>
      </StyledDiv>,
      <StyledDiv key={'forgot-password-step2'} maxWidth='31.25rem'>
        <InfoComponent
          title='Check your inbox!'
          description='Use the link on the email we just sent to create a new password.'
          image='/images/mail-illustration.svg'
          imageAlt='Mail Illustration'
          hideButtonOnDesktop={true}
          buttonMessage='Open Mail App'
          buttonOnClick={() => {
            window.location.href = 'mailto:'
          }}
          secondButtonOnClick={handleBackStep}
          secondButtonMessage={`Didn't receive anything? Resend the email`}
        />
      </StyledDiv>,
    ]
  }, [control, errors, failedMessage, handleBackStep, handleSubmit, isSubmitting, onSubmit, theme.palette.role.primary])

  return (
    <>
      <Body steps={steps} handleBackStep={handleBackStep} activeStep={activeStep} hideBackButton={true} />
      <Footer>
        Don&apos;t have an account?{' '}
        <StyledLink to='/sign-up' onClick={clearAll}>
          Sign Up
        </StyledLink>
      </Footer>
    </>
  )
}
